var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import md5 from 'blueimp-md5/js/md5.min';
var DriveService = /** @class */ (function () {
    function DriveService(options) {
        this.errors = {
            'file/no-id': 'No id.',
            'file/invalid-file-resource': 'No fileResource or invalid format.'
        };
        this.options = __assign({ urlPrefix: 'https://drive.google.com/uc?id=', urlSuffix: '&export=download' }, options);
    }
    DriveService.prototype.registerRoutes = function (options) {
        var _this = this;
        var router = options.router, _a = options.endpoint, endpoint = _a === void 0 ? 'storage' : _a, _b = options.disabledRoutes, disabledRoutes = _b === void 0 ? [
            'post:/' + endpoint,
            'put:/' + endpoint,
        ] : _b, _c = options.middlewares, middlewares = _c === void 0 ? [function (req, res, next) { return next(); }] : _c;
        // register errors & disabled routes
        router.setDisabled(disabledRoutes);
        router.setErrors(this.errors);
        // get file information
        router.get.apply(router, ['/' + endpoint].concat(middlewares, [function (req, res) {
                var id = req.query.id;
                var result;
                try {
                    result = _this.get(id);
                }
                catch (code) {
                    return res.error(code);
                }
                return res.success(result);
            }]));
        // upload a file
        var uploader = function (req, res) {
            var fileResource = req.body.fileResource;
            var customFolder = req.body.customFolder;
            var rename = req.body.rename;
            var result = {};
            try {
                result = _this.upload(fileResource, customFolder, rename);
            }
            catch (code) {
                return res.error(code);
            }
            return res.success(result);
        };
        router.post.apply(router, ['/' + endpoint].concat(middlewares, [uploader]));
        router.put.apply(router, ['/' + endpoint].concat(middlewares, [uploader]));
    };
    DriveService.prototype.get = function (id) {
        if (!id) {
            throw new Error('file/no-id');
        }
        var _a = this.options, urlPrefix = _a.urlPrefix, urlSuffix = _a.urlSuffix;
        // get the file
        var file = DriveApp.getFileById(id);
        // check permission
        if (!this.hasPermission(file)) {
            throw new Error('file/not-allowed');
        }
        // response
        var fileId = file.getId();
        var name = file.getName();
        var mimeType = file.getMimeType();
        var description = file.getDescription();
        var size = file.getSize();
        var link = file.getUrl();
        return {
            id: fileId, name: name, mimeType: mimeType, description: description, size: size, link: link,
            url: urlPrefix + id + urlSuffix
        };
    };
    DriveService.prototype.upload = function (fileResource, customFolder, rename, sharing) {
        if (sharing === void 0) { sharing = {}; }
        if (!fileResource ||
            !(fileResource instanceof Object) ||
            !fileResource.base64Data ||
            !fileResource.size ||
            !fileResource.name) {
            throw new Error('file/invalid-file-resource');
        }
        var contentFolderId = this.options.contentFolder;
        var access = sharing.access, permission = sharing.permission;
        // get the content & uploads folder
        var folder = DriveApp.getFolderById(contentFolderId);
        folder = this.getFolderByName(folder, 'uploads');
        // custom folder
        if (customFolder) {
            folder = this.getFolderByName(folder, customFolder);
        }
        else {
            var date = new Date();
            var year = '' + date.getFullYear();
            var month = date.getMonth() + 1;
            month = '' + (month < 10 ? '0' + month : month);
            folder = this.getFolderByName(folder, year);
            folder = this.getFolderByName(folder, month);
        }
        var fileName = fileResource.name;
        var fileExt = fileName.split('.').pop();
        // check if new name includes the extension
        if (rename) {
            fileName = (rename.indexOf(fileExt) > -1) ? rename : rename + '.' + fileExt;
        }
        if (rename === 'MD5') {
            fileName = md5(fileName) + '.' + fileExt;
        }
        if (rename === 'AUTO') {
            fileName = Utilities.getUuid() + '.' + fileExt;
        }
        // mimeType and base64Content
        var _a = this.base64Breakdown(fileResource.base64Data), fileMimeType = _a.mimeType, base64Content = _a.base64Content;
        // save the file
        var newFile = folder.createFile(Utilities.newBlob(Utilities.base64Decode(base64Content, Utilities.Charset.UTF_8), fileMimeType, fileName));
        // set sharing
        newFile.setSharing(DriveApp.Access[(access || 'ANYONE_WITH_LINK').toUpperCase()], DriveApp.Permission[(permission || 'VIEW').toUpperCase()]);
        // response
        var id = newFile.getId();
        var name = newFile.getName();
        var mimeType = newFile.getMimeType();
        var description = newFile.getDescription();
        var size = newFile.getSize();
        var link = newFile.getUrl();
        return {
            id: id, name: name, mimeType: mimeType, description: description, size: size, link: link,
            url: this.options.urlPrefix + id + this.options.urlSuffix
        };
    };
    DriveService.prototype.getFolderByName = function (parentFolder, folderName) {
        var folder = parentFolder;
        var childFolders = folder.getFoldersByName(folderName);
        if (!childFolders.hasNext()) {
            folder = folder.createFolder(folderName);
        }
        else {
            folder = childFolders.next();
        }
        return folder;
    };
    DriveService.prototype.hasPermission = function (file) {
        // check sharing
        var access = file.getSharingAccess();
        if (access !== DriveApp.Access.ANYONE &&
            access !== DriveApp.Access.ANYONE_WITH_LINK) {
            return false;
        }
        // is in the cotent folder
        var contentFolderId = this.options.contentFolder;
        var folders = file.getParents();
        var folderIds = [];
        while (folders.hasNext()) {
            folderIds.push(folders.next().getId());
        }
        if (folderIds.indexOf(contentFolderId) < 0) {
            return false;
        }
        return true;
    };
    DriveService.prototype.base64Breakdown = function (base64Data) {
        var _a = base64Data.split(';base64,'), mimeTypeData = _a[0], base64Content = _a[1];
        if (!mimeTypeData || !base64Content) {
            throw new Error('Malform base64 data.');
        }
        return {
            mimeType: mimeTypeData.replace('data:', ''),
            base64Content: base64Content
        };
    };
    return DriveService;
}());
export { DriveService };
